'use strict';

/**
 * TODO: refactor for:
 *    - better differentiation between async/sync functions
 *    - caching group
 */
export const GroupAuthService = [
  '$http',
  '$q',
  '$state',
  'moment',
  'AuthSvc',
  'GrpRoleTypeSvc',
  'GrpRoleSvc',
  function(
    $http,
    $q,
    $state,
    moment,
    AuthSvc,
    GrpRoleTypeSvc,
    GrpRoleSvc
  ) {
    var permissions = {};
    var refreshInt = 24 * 60; // minutes between refreshing permissions

    // cache user information - additive with each request
    var currentUserId;
    var admin = false;
    var userGroupRoles = {}; // structure: {groupId: {roles, setAt}}
    var groupPromises = {}; // check for outstanding group requests

    /**
     * Pull application permissions from the server
     */
    function setPermissions(groupId) {
      return $q(function(resolve) {
        //validate permissions have been set
        if(!permissions[groupId]) {
          return GrpRoleTypeSvc.getPermissions({groupId}).$promise
            .then(servPerms => { permissions[groupId] = servPerms; })
            .then(resolve);
        }
        resolve();
      });
    }

    /**
     * Set currentUserId and admin
     * @param {Object} user
     */
    function setUser(user) {
      currentUserId = user ? user.id : null;
      admin = user && user.role === 'admin';
    }

    /**
     * Get current user id (reset if not found)
     * @return {String}   - return userId if there is a match
     */
    function getUserId() {
      var user = AuthSvc.getCurrentUserSync();
      if(!user || !user.id) {
        resetAll();
        return null;
      } // no user
      if(user.id === currentUserId) { return currentUserId; }
      resetAll();
      setUser(user);
      return user.id;
    }

    /**
     * Retrieve group roles from the server
     * @param  {String} groupId
     * @return {Promise}
     */
    function fetchUserRolesForGroup(groupId) {
      var userId = getUserId();
      if(!userId) { return []; }
      if(!groupPromises[groupId]) {
        groupPromises[groupId] = GrpRoleSvc.getForGroupUser({groupId, userId})
          .$promise.then(groupRoles => {
            delete groupPromises[groupId];
            setUserGroupRoles(groupId, groupRoles);
            return groupRoles;
          });
      }
      var grpPromise = groupPromises[groupId];
      return grpPromise;
    }

    /**
     * Set userGroupRoles for a group
     * @param {String} groupId
     * @param {Array|String} groupRole - roles to set
     */
    function setUserGroupRoles(groupId, groupRole) {
      // process array of groupRole
      if(Array.isArray(groupRole)) {
        for(var i = 0; i < groupRole.length; i++) {
          setUserGroupRoles(groupId, groupRole[i]);
        }
        return;
      }
      if(!userGroupRoles[groupId]) { userGroupRoles[groupId] = {}; }
      var grpRoles = userGroupRoles[groupId];
      if(!grpRoles.rolesArray) { grpRoles.rolesArray = []; }
      if(groupRole && grpRoles.rolesArray.indexOf(groupRole)) {
        grpRoles.rolesArray.push(groupRole);
      }
      userGroupRoles[groupId].setAt = moment();
    }

    /**
     * Check if the group permisisons expired (requires refresh from server)
     * @param  {Object}  group - current group instance from userGroupRoles
     * @return {Boolean}       - if group is non-existent or expired
     */
    function isGroupExpired(group) {
      return !group
          || !group.setAt
          || moment().diff(group.setAt, 'minutes') > refreshInt;
    }

    /**
     * Get a group's roles for the current user
     * @param  {String} groupId
     * @return {Array}          - Array of GroupRoles
     *
     * If group has expired, will repull the groupRoles
     */
    function getUserGroupRoles(groupId) {
      return $q(function(resolve) {
        var grp = userGroupRoles[groupId];
        if(isGroupExpired(grp)) {
          var gr = fetchUserRolesForGroup(groupId);
          /**
           * TODO: fix when refactoring the entire group auth service
           * temporary hack: check if array is returned instead of promise
           * this check prevents redirect to /login when authenticated,
           * but not authorized
           */
          if(Array.isArray(gr)) { return resolve(gr); }
          return gr.then(resolve);
        }
        resolve(grp.rolesArray);
      });
    }

    /**
     * Sync - Get a group's roles for the current user
     * @param  {String} groupId
     * @return {Array}          - Array of GroupRoles
     *
     * If group has expired, will repull the groupRoles asynchronously,
     *  this will reset for future requests, but not the current one.
     */
    function getUserGroupRolesSync(groupId) {
      // check that group is set and still valid
      var grp = userGroupRoles[groupId];
      if(isGroupExpired(grp)) {
        //refresh group roles for next run
        fetchUserRolesForGroup(groupId);
      }
      return grp ? grp.rolesArray : [];
    }

    /**
     * Reset GroupAuthSvc cached user and groupRoles
     */
    function resetAll() {
      setUser();
      userGroupRoles = {};
    }

    /**
     * Get user's full permission string for a group and object
     * @param  {String} groupId
     * @param  {String} object
     * @return {String}         - Full string with all permissions
     */
    function getPermissionsStr(groupId, object) {
      var userRoles = getUserGroupRolesSync(groupId);
      var permStr = '';
      if(!permissions || !permissions[groupId]) { return permStr; }
      var objPerms = permissions[groupId][object];
      if(!objPerms) {return permStr; }
      for(var i in userRoles) {
        var role = userRoles[i];
        permStr += typeof objPerms[role] === 'string' ? objPerms[role] : '';
      }
      return permStr;
    }

    function checkPermissionString(permStr, permissionLetter) {
      return permStr.indexOf(permissionLetter) !== -1;
    }

    var GroupAuthSvc = {
      /**
       * Check if user has permission only
       *
       * @param {String}   groupId     - user's group
       * @param {String}   object      - required permission object
       * @param {String}   permission  - required permission letter
       * @return {Boolean}
       */
      hasPermission(groupId, object, permission) {
        getUserId();
        if(admin) { return true; }
        var permStr = getPermissionsStr(groupId, object);
        return checkPermissionString(permStr, permission);
      },

      /**
       * Check if user has permission
       * @param  {String} groupId       - user's group
       * @param  {String} object        - required permission object
       * @param  {String} reqPermission - (optional) permission to
       *                                validate first
       * @return {Function}
       */
      getPermissionChecker(groupId, object, reqPermission) {
        return setPermissions(groupId)
          .then(function() { return AuthSvc.getCurrentUser(); })
          .then(function(user) {
          /**
           * check for current user and reset if not
           * compensates for unusual async/sync usage throughout
           * TODO: refactor - organize and better sync/async calls
           */
            if(user.id !== currentUserId) { resetAll(); }
            return getUserGroupRoles(groupId);
          })
          .then(function() {
          // if required permission provided, check and reroute if necessary
            if(reqPermission) {
              GroupAuthSvc.validatePermission(groupId, object, reqPermission);
            }
            // args can be (objectOverwrite, permission) or (permission)
            return function(arg1, arg2) {
              var objOverwrite = arg2 ? arg1 : object;
              var perm = arg2 || arg1;
              return GroupAuthSvc.hasPermission(groupId, objOverwrite, perm);
            };
          });
      },

      /**
      * Reset and reroute if a permission is not met
      *
      * @param  {Boolean}  permissionFound - user has valid permission
      */
      rerouteHome(permissionFound) {
        if(!permissionFound) {
          resetAll();
          $state.go('main');
        }
      },

      /**
       * Check for permission and reroute if not found
       *
       * @param  {String}   groupId     - user's group
       * @param  {String}   object      - required permission object
       * @param  {String}   permission  - required permission letter
       * @return {Boolean}
       */
      validatePermission(groupId, object, permission) {
        var hasPerm = GroupAuthSvc.hasPermission(groupId, object, permission);
        if(!hasPerm) {
          GroupAuthSvc.rerouteHome();
        }
        return hasPerm;
      }
    };

    return GroupAuthSvc;
  }
];
